import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gateway: auto-scope all /api/admin/ requests to the admin's selected panel language.
// GET -> ?language=, POST/PUT -> body.language. Registered once on module import.
axios.interceptors.request.use((config) => {
  try {
    const url = config.url || "";
    if (url.includes("/api/admin/")) {
      const lang = localStorage.getItem("admin_lang");
      if (lang) {
        const method = (config.method || "get").toLowerCase();
        if (method === "get") {
          // Respect an explicitly-provided language param (e.g. cross-language admin lookups).
          const params = { ...(config.params || {}) };
          if (params.language === undefined) params.language = lang;
          config.params = params;
        } else if (method === "post" || method === "put") {
          if (config.data && typeof config.data === "object" && config.data.language === undefined) {
            config.data = { ...config.data, language: lang };
          }
        }
      }
    }
  } catch (e) {}
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, role } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    // Fetch user to get native_language and set it
    try {
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(meRes.data);
      if (meRes.data.native_language) {
        localStorage.setItem("app_language", meRes.data.native_language);
      }
    } catch (e) {}
    return { role };
  };

  const register = async (email, password, fullName, identityNo, nativeLanguage, learningLanguage) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
      identity_no: identityNo,
      native_language: nativeLanguage,
      learning_language: learningLanguage || "en"
    });
    const { token: newToken } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("admin_lang");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser,
      getAuthHeader,
      isAdmin: user?.role === "admin"
    }}>
      {children}
    </AuthContext.Provider>
  );
};
